import React, { useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ApartmentOutlined,
  Close,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  NotesOutlined,
  PaymentsOutlined,
  PersonOutlined,
  PhotoLibraryOutlined,
  PlaceOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/material/styles";
import SelectedPhotos from "./SelectedPhotos";
import FormSection from "./FormSection";
import PhotoGrid from "./PhotoGrid";
import { startOfToday } from "../utils/dates";
import { radius, tokens } from "../theme/theme";

const HiddenInput = styled("input")({ display: "none" });

/** Every field the save button insists on. Mirrors AddRealEstateObject. */
const REQUIRED = ["street", "region", "rooms", "floor", "cost", "heating"];

const EditRealEstateObject = ({
  onEditItem,
  onCloseDialog,
  regionSearchTerms,
  streetSearchTerms,
  item,
  albumPhotos,
  albumLoading,
  onReorderAlbum,
  onDeleteAlbumPhoto,
}) => {
  const inputFile = useRef(null);
  const [inputFiles, setInputFiles] = useState([]);
  const [personName, setPersonName] = useState(item.personName);
  const [phoneNumber, setPhoneNumber] = useState(item.phoneNumber);
  const [rooms, setRooms] = useState(item.rooms);
  const [cost, setCost] = useState(item.cost);
  const [currency, setCurrency] = useState(item.currency);
  const [street, setStreet] = useState(item.street);
  const [region, setRegion] = useState(item.region);
  const [floor, setFloor] = useState(item.floor);
  const [state, setState] = useState(item.state);
  const [heating, setHeating] = useState(item.heating);
  const [info, setInfo] = useState(item.info);
  const [furniture, setFurniture] = useState(item.furniture);
  const [technology, setTechnology] = useState(item.technology);
  const [displayDateFrom, setDisplayDateFrom] = useState(
    new Date(new Date().setTime(item.dateFrom))
  );

  // Only shown after a failed save attempt — see AddRealEstateObject.
  const [showErrors, setShowErrors] = useState(false);

  const values = {
    street: street,
    region: region,
    rooms: rooms,
    floor: floor,
    cost: cost,
    heating: heating,
  };
  const missing = REQUIRED.filter(function (key) {
    return values[key] === "" || values[key] === null;
  });

  const handleFileInput = (e) => {
    var selectedFiles = [];
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    for (var i = 0; i < e.target.files.length; i++) {
      selectedFiles.push(e.target.files[i]);
    }
    setInputFiles([...inputFiles, ...selectedFiles]);
    e.target.value = "";
  };

  const removeFile = (fileVar) => {
    setInputFiles(inputFiles.filter((file) => file !== fileVar));
  };

  const onSave = (e) => {
    e.preventDefault();

    item = {
      id: item.id,
      personName: personName,
      phoneNumber: phoneNumber,
      rooms: rooms,
      dateFrom: displayDateFrom.getTime(),
      dateEdit: startOfToday(),
      cost: cost,
      currency: currency,
      street: street,
      region: region,
      floor: floor,
      state: state,
      heating: heating,
      info: info,
      furniture: furniture,
      technology: technology,
    };
    onEditItem(item, inputFiles);
    onCloseDialog();
  };

  const trySave = (e) => {
    if (missing.length === 0) {
      onSave(e);
    } else {
      setShowErrors(true);
    }
  };

  const noEnter = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const required = (value) => ({
    error: showErrors && value === "",
    helperText: showErrors && value === "" ? "Заповніть це поле" : " ",
  });

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="md"
      scroll="paper"
      onClose={onCloseDialog}
      PaperProps={{ sx: { borderRadius: radius.lg } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid " + tokens.line,
          py: 2,
        }}
      >
        <EditOutlined sx={{ color: tokens.blue600 }} />
        <Box sx={{ flexGrow: 1, lineHeight: 1.2 }}>
          <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
            Редагувати оголошення
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.ink400 }}>
            {[item.street, item.region].filter(Boolean).join(", ") || item.id}
          </Typography>
        </Box>
        <IconButton onClick={onCloseDialog} aria-label="Закрити">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: tokens.canvas }}>
        <Box component="form" noValidate autoComplete="off" sx={{ pt: 3 }}>
          <FormSection title="Адреса" icon={<PlaceOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  freeSolo
                  inputValue={street}
                  options={streetSearchTerms.map((option) => option)}
                  onInputChange={(e, newValue) => setStreet(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Вулиця *"
                      {...required(street)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  inputValue={region}
                  options={regionSearchTerms.map((option) => option)}
                  onInputChange={(e, newValue) => setRegion(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Район *"
                      {...required(region)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Ціна" icon={<PaymentsOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="edit-cost"
                  label="Ціна *"
                  variant="outlined"
                  type="number"
                  defaultValue={cost}
                  onKeyPress={noEnter}
                  onChange={(e) => setCost(e.target.value)}
                  fullWidth
                  {...required(cost)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  inputValue={currency}
                  options={["грн", "$"]}
                  onInputChange={(e, newValue) => setCurrency(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Валюта"
                      helperText=" "
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    inputFormat="dd.MM.yyyy"
                    mask="__.__.____"
                    value={displayDateFrom}
                    onChange={(newValue) => setDisplayDateFrom(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "дд.мм.рррр",
                        }}
                        onKeyPress={noEnter}
                        fullWidth
                        variant="outlined"
                        label="Дата заповнення"
                        helperText=" "
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            title="Характеристики"
            icon={<ApartmentOutlined fontSize="small" />}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  id="edit-rooms"
                  label="Кімнат *"
                  variant="outlined"
                  type="number"
                  defaultValue={rooms}
                  onKeyPress={noEnter}
                  onChange={(e) => setRooms(e.target.value)}
                  fullWidth
                  {...required(rooms)}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  id="edit-floor"
                  label="Поверх *"
                  variant="outlined"
                  type="number"
                  defaultValue={floor}
                  onKeyPress={noEnter}
                  onChange={(e) => setFloor(e.target.value)}
                  fullWidth
                  {...required(floor)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  inputValue={state}
                  options={["Санітарний", "Під Євро", "Євро"]}
                  onInputChange={(e, newValue) => setState(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Стан"
                      helperText=" "
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  inputValue={heating}
                  options={["Центральне", "Індивідуальне"]}
                  onInputChange={(e, newValue) => setHeating(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Опалення *"
                      {...required(heating)}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={<Checkbox checked={!!furniture} />}
                label="Меблі"
                onChange={(e) => setFurniture(e.currentTarget.checked)}
              />
              <FormControlLabel
                control={<Checkbox checked={!!technology} />}
                label="Техніка"
                onChange={(e) => setTechnology(e.currentTarget.checked)}
              />
            </Stack>
          </FormSection>

          <FormSection title="Власник" icon={<PersonOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="edit-name"
                  label="Ім'я власника"
                  variant="outlined"
                  type="text"
                  defaultValue={personName}
                  onKeyPress={noEnter}
                  onChange={(e) => setPersonName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="edit-phone"
                  label="Номер телефону"
                  variant="outlined"
                  type="text"
                  defaultValue={phoneNumber}
                  onKeyPress={noEnter}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ color: tokens.ink400 }}>
              Не показується на публічному сайті.
            </Typography>
          </FormSection>

          <FormSection title="Опис" icon={<NotesOutlined fontSize="small" />}>
            <TextField
              id="edit-info"
              placeholder="Ставте посилання в [ ]"
              label="Додаткова інформація"
              variant="outlined"
              type="text"
              defaultValue={info}
              onChange={(e) => setInfo(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </FormSection>

          {/* The listing's existing album, in the dialog where a realtor is
              already changing everything else about it. Same grid the Photos
              dialog uses, so the two cannot drift apart. */}
          <FormSection
            title="Фото об'єкта"
            icon={<PhotoLibraryOutlined fontSize="small" />}
          >
            {albumLoading && <LinearProgress sx={{ mb: 1.5 }} />}
            {!albumLoading && (!albumPhotos || albumPhotos.length === 0) && (
              <Typography variant="caption" sx={{ color: tokens.ink400 }}>
                Для цього оголошення ще немає фото.
              </Typography>
            )}
            {albumPhotos && albumPhotos.length > 0 && (
              <React.Fragment>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 1.5, color: tokens.ink400 }}
                >
                  Перетягніть фото або скористайтесь ‹ ›, щоб змінити порядок.
                  Перше фото — головне: воно показується на картці й у
                  посиланнях.
                </Typography>
                <PhotoGrid
                  photos={albumPhotos}
                  onReorder={onReorderAlbum}
                  actions={
                    onDeleteAlbumPhoto
                      ? [
                          {
                            key: "delete",
                            label: "Видалити фото",
                            icon: function () {
                              return <DeleteOutlined sx={{ fontSize: 18 }} />;
                            },
                            onClick: onDeleteAlbumPhoto,
                            sx: {
                              backgroundColor: "rgba(255,255,255,0.92)",
                              "&:hover": {
                                backgroundColor: tokens.red600,
                                color: tokens.surface,
                              },
                            },
                          },
                        ]
                      : []
                  }
                />
              </React.Fragment>
            )}
          </FormSection>

          <FormSection
            title="Додати фото"
            icon={<CloudUploadOutlined fontSize="small" />}
            sx={{ mb: 1 }}
          >
            <HiddenInput
              multiple
              accept="image/*"
              id="edit-photo-input"
              type="file"
              ref={inputFile}
              onChange={handleFileInput}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadOutlined />}
              onClick={() => inputFile.current && inputFile.current.click()}
            >
              Вибрати файли
            </Button>
            <SelectedPhotos files={inputFiles} onRemove={removeFile} />
          </FormSection>

          {showErrors && missing.length > 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Заповніть обов'язкові поля, позначені зірочкою.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid " + tokens.line, gap: 1 }}
      >
        <Button variant="text" onClick={onCloseDialog}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<SaveOutlined />}
          onClick={trySave}
        >
          Зберегти зміни
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRealEstateObject;
